import type {
  TowerDataFile,
  EnemyDataFile,
  WaveDataFile,
  SynergyDataFile,
  CardDataFile,
} from "../entities/types.ts";
import { TOWER_SPECIAL_TYPES, ENEMY_SPECIAL_TYPES } from "../entities/types.ts";
import type { ModuleDataFile } from "../systems/ModuleRegistry.ts";
import type { OperatorsDataFile } from "../systems/OperatorEffects.ts";
import type { ThreatLevelsDataFile } from "../systems/ThreatEffects.ts";
import type { SpawnPolicy } from "../core/config.ts";
import {
  TOWER_PALETTE,
  ENEMY_PALETTE,
  SYNERGY_PALETTE,
  DEFENSE,
} from "../core/palette.ts";

export interface FirewallDataFile {
  default: {
    cost: number;
    hp: number;
    maxHp: number;
    color: string;
    colorLight: string;
  };
  /** D-322 / AC-5 (Task-007) — the in-run firewall placement cap
   *  (`entities/Firewall.ts` `FIREWALL_CAP`). Absent → the byte-safe code
   *  default (3). */
  maxFirewalls?: number;
}

export interface GameData {
  towers: TowerDataFile;
  enemies: EnemyDataFile;
  waves: WaveDataFile;
  synergies: SynergyDataFile;
  cards: CardDataFile;
  firewalls: FirewallDataFile;
  modules: ModuleDataFile;
  /**
   * BL-145 / D-247 — the optional data-driven spawn-policy override (the
   * `spawn` key). Absent (no `spawn.json` ships this session) → createGrid /
   * BreachSystem fall back to `DEFAULT_SPAWN_POLICY`, byte-identical. A partial
   * override replaces only the named fields; the *capability* lands now, the
   * *data* lands with the BL-146 geography. Resolved via `resolveSpawnPolicy`.
   */
  spawn?: Partial<SpawnPolicy> | undefined;
  /**
   * D-322 / AC-5 (Task-007) — the optional data-driven operators override
   * (the `operators` key, `operators.json`). Absent (tolerant of no file
   * shipping) → `OperatorEffects` runs on its byte-identical code defaults.
   * Resolved via `applyOperatorsData`.
   */
  operators?: OperatorsDataFile | undefined;
  /**
   * Chain step A (2026-08-18) — the optional data-driven threat-profile
   * override (the `threatLevels` key, `threat-levels.json`). Absent →
   * `ThreatEffects` runs on its byte-identical code defaults (containment
   * anchor included). Resolved via `applyThreatLevelsData`.
   */
  threatLevels?: ThreatLevelsDataFile | undefined;
}

export async function loadGameData(basePath = "data"): Promise<GameData> {
  const [towers, enemies, waves, synergies, cards, firewalls, modules] =
    await Promise.all([
      fetchJson<TowerDataFile>(`${basePath}/towers.json`),
      fetchJson<EnemyDataFile>(`${basePath}/enemies.json`),
      fetchJson<WaveDataFile>(`${basePath}/waves.json`),
      fetchJson<SynergyDataFile>(`${basePath}/synergies.json`),
      fetchJson<CardDataFile>(`${basePath}/cards.json`),
      fetchJson<FirewallDataFile>(`${basePath}/firewalls.json`),
      fetchJson<ModuleDataFile>(`${basePath}/modules.json`),
    ]);

  // BL-145: the OPTIONAL spawn override. Tolerant of absence (no file ships) —
  // an absent/unreadable `spawn.json` resolves to `undefined`, so the default
  // policy applies and nothing changes. This is the wired-but-unpopulated seam.
  const spawn = await fetchOptionalJson<Partial<SpawnPolicy>>(
    `${basePath}/spawn.json`
  );

  // D-322 / AC-5 (Task-007): the OPTIONAL operators override, same tolerant
  // pattern as `spawn.json` above — absent/unreadable → `undefined`, so the
  // byte-identical `OperatorEffects` code defaults apply.
  const operators = await fetchOptionalJson<OperatorsDataFile>(
    `${basePath}/operators.json`
  );

  // Chain step A: the OPTIONAL threat-profile override, same tolerant pattern.
  const threatLevels = await fetchOptionalJson<ThreatLevelsDataFile>(
    `${basePath}/threat-levels.json`
  );

  const data: GameData = {
    towers,
    enemies,
    waves,
    synergies,
    cards,
    firewalls,
    modules,
    spawn,
    operators,
    threatLevels,
  };
  applyPalette(data);
  return data;
}

/**
 * Inject gameplay-entity colours from the central palette (core/palette.ts,
 * BL-52). The data files carry NO colour — this is the single seam where
 * towers/enemies/synergies/firewall resolve their colour, keyed by element /
 * enemy-id / synergy-id, so the palette stays the one source of truth. Unknown
 * keys (e.g. test mocks) are left untouched; the real shipped keys are
 * guaranteed by the DataSchema + palette tests. Colour is render-only — the
 * sim never reads it, so this is determinism-neutral.
 */
function applyPalette(data: GameData): void {
  for (const [element, tower] of Object.entries(data.towers)) {
    const p = TOWER_PALETTE[element];
    if (p) {
      tower.color = p.color;
      tower.colorLight = p.colorLight;
    }
  }
  for (const [id, enemy] of Object.entries(data.enemies)) {
    const c = ENEMY_PALETTE[id];
    if (c) enemy.color = c;
  }
  for (const syn of data.synergies.synergies) {
    const p = SYNERGY_PALETTE[syn.id];
    if (p) {
      syn.color = p.color;
      syn.colorLight = p.colorLight;
    }
  }
  data.firewalls.default.color = DEFENSE.firewall.color;
  data.firewalls.default.colorLight = DEFENSE.firewall.colorLight;
}

async function fetchJson<T>(url: string): Promise<T> {
  const response = await fetch(url);
  if (!response.ok) {
    throw new Error(`Failed to load ${url}: ${response.status}`);
  }
  const parsed = (await response.json()) as unknown;
  validateJsonShape(url, parsed);
  return parsed as T;
}

/**
 * BL-145: fetch an OPTIONAL JSON override. Unlike {@link fetchJson} this never
 * throws on absence — a missing/unreadable file (404, parse error) resolves to
 * `undefined` so the caller falls back to its typed default. Mirrors the
 * tolerant optional-fetch pattern already used for the audio manifest.
 */
async function fetchOptionalJson<T>(url: string): Promise<T | undefined> {
  try {
    const response = await fetch(url);
    if (!response.ok) return undefined;
    return (await response.json()) as T;
  } catch {
    return undefined;
  }
}

function validateJsonShape(url: string, data: unknown): void {
  const file = url.split("/").pop() ?? url;
  switch (file) {
    case "towers.json":
      validateTowers(data, file);
      return;
    case "enemies.json":
      validateEnemies(data, file);
      return;
    case "waves.json":
      validateWaves(data, file);
      return;
    case "synergies.json":
      validateSynergies(data, file);
      return;
    case "cards.json":
      validateCards(data, file);
      return;
    case "firewalls.json":
      validateFirewalls(data, file);
      return;
    case "modules.json":
      validateModules(data, file);
      return;
    default:
      return;
  }
}

function invalid(file: string, path: string, message: string): never {
  throw new Error(`Invalid ${file}: "${path}" ${message}`);
}

function isRecord(v: unknown): v is Record<string, unknown> {
  return typeof v === "object" && v !== null;
}

function assertRecord(
  file: string,
  path: string,
  v: unknown
): Record<string, unknown> {
  if (!isRecord(v)) invalid(file, path, "is not an object");
  return v;
}

function assertNumber(file: string, path: string, v: unknown): number {
  if (typeof v !== "number" || Number.isNaN(v)) {
    invalid(file, path, "is not a number");
  }
  return v;
}

function assertPositive(file: string, path: string, v: unknown): number {
  const n = assertNumber(file, path, v);
  if (n <= 0) invalid(file, path, "must be > 0");
  return n;
}

function assertNonNegative(file: string, path: string, v: unknown): number {
  const n = assertNumber(file, path, v);
  if (n < 0) invalid(file, path, "must be >= 0");
  return n;
}

function assertString(file: string, path: string, v: unknown): string {
  if (typeof v !== "string" || v.length === 0) {
    invalid(file, path, "is not a non-empty string");
  }
  return v;
}

function assertOneOf<T extends string>(
  file: string,
  path: string,
  v: unknown,
  allowed: readonly T[]
): T {
  const actual = assertString(file, path, v);
  if (!allowed.includes(actual as T)) {
    invalid(file, path, `must be one of [${allowed.join(", ")}]`);
  }
  return actual as T;
}

function assertArray(file: string, path: string, v: unknown): unknown[] {
  if (!Array.isArray(v)) invalid(file, path, "is not an array");
  return v;
}

function validateTowers(data: unknown, file: string): void {
  const towers = assertRecord(file, "towers", data);
  const entries = Object.entries(towers);
  if (entries.length === 0) invalid(file, "towers", "is empty");

  for (const [id, raw] of entries) {
    const tower = assertRecord(file, id, raw);
    assertPositive(file, `${id}.cost`, tower.cost);
    assertNumber(file, `${id}.damage`, tower.damage);
    assertPositive(file, `${id}.range`, tower.range);
    assertPositive(file, `${id}.fireRate`, tower.fireRate);

    if (tower.special !== undefined && tower.special !== null) {
      const special = assertRecord(file, `${id}.special`, tower.special);
      assertOneOf(
        file,
        `${id}.special.type`,
        special.type,
        TOWER_SPECIAL_TYPES
      );
    }
  }
}

function validateEnemies(data: unknown, file: string): void {
  const enemies = assertRecord(file, "enemies", data);
  const entries = Object.entries(enemies);
  if (entries.length === 0) invalid(file, "enemies", "is empty");

  for (const [id, raw] of entries) {
    const enemy = assertRecord(file, id, raw);
    assertPositive(file, `${id}.hp`, enemy.hp);
    assertPositive(file, `${id}.speed`, enemy.speed);
    assertNonNegative(file, `${id}.reward`, enemy.reward);

    // BL-51: optional render-only silhouette family. Validate the type only
    // (a non-empty string); an unknown value safely falls back to the diamond
    // in EnemyRenderer, so the renderer — not the loader — owns the registry.
    if (enemy.shape !== undefined) {
      assertString(file, `${id}.shape`, enemy.shape);
    }

    if (enemy.special !== undefined && enemy.special !== null) {
      const special = assertRecord(file, `${id}.special`, enemy.special);
      assertOneOf(
        file,
        `${id}.special.type`,
        special.type,
        ENEMY_SPECIAL_TYPES
      );
    }
  }
}

function validateWaves(data: unknown, file: string): void {
  const root = assertRecord(file, "waves", data);
  // WaveDataFile / WaveManager read `templates` (NOT `waves`). The previous
  // `root.waves` check never matched the shipped file and crashed boot at
  // runtime while the mock-based test stayed green — guarded now by the
  // real-data validation test in tests/DataLoader.test.ts.
  const templates = assertArray(file, "waves.templates", root.templates);
  if (templates.length === 0) invalid(file, "waves.templates", "is empty");

  // S156 / D-253 — the OPTIONAL central economy block (the modularity lift).
  // Absent → the EconomySystem code default (byte-identical, identity ramp). If
  // present, validate the numeric fields so a typo'd re-tune fails LOUD at boot
  // rather than silently disabling the cap/ramp.
  if (root.economy !== undefined && root.economy !== null) {
    const eco = assertRecord(file, "waves.economy", root.economy);
    for (const key of [
      "surplusThreshold",
      "surplusRange",
      "surplusFloor",
      "capStartWave",
    ] as const) {
      if (eco[key] !== undefined) {
        assertNonNegative(file, `waves.economy.${key}`, eco[key]);
      }
    }
    if (eco.killGoldRamp !== undefined && eco.killGoldRamp !== null) {
      const ramp = assertRecord(
        file,
        "waves.economy.killGoldRamp",
        eco.killGoldRamp
      );
      for (const key of ["fullUntilWave", "floor", "floorWave"] as const) {
        if (ramp[key] !== undefined) {
          assertNonNegative(
            file,
            `waves.economy.killGoldRamp.${key}`,
            ramp[key]
          );
        }
      }
    }
    if (eco.waveBonus !== undefined && eco.waveBonus !== null) {
      const wb = assertRecord(file, "waves.economy.waveBonus", eco.waveBonus);
      for (const key of [
        "base",
        "perWave",
        "lateStartWave",
        "latePerWave",
      ] as const) {
        if (wb[key] !== undefined) {
          assertNonNegative(file, `waves.economy.waveBonus.${key}`, wb[key]);
        }
      }
    }
    if (eco.sellMultiplier !== undefined) {
      assertNonNegative(
        file,
        "waves.economy.sellMultiplier",
        eco.sellMultiplier
      );
    }
  }

  // D-322 / AC-5 (Task-007) — the OPTIONAL shield tuning block. Absent → the
  // config.ts code default. Same shallow-numeric-field validation depth as
  // the economy block above.
  if (root.shield !== undefined && root.shield !== null) {
    const shield = assertRecord(file, "waves.shield", root.shield);
    for (const key of [
      "startWave",
      "CHANCE_MIN",
      "CHANCE_MAX",
      "HP_FRAC_MIN",
      "HP_FRAC_MAX",
      "EMP_MULT",
    ] as const) {
      if (shield[key] !== undefined) {
        assertNonNegative(file, `waves.shield.${key}`, shield[key]);
      }
    }
  }

  // D-322 / AC-5 (Task-007) — the OPTIONAL combo tuning block. Absent → the
  // ScoreSystem code default.
  if (root.score !== undefined && root.score !== null) {
    const score = assertRecord(file, "waves.score", root.score);
    if (score.comboTimeoutMs !== undefined) {
      assertPositive(file, "waves.score.comboTimeoutMs", score.comboTimeoutMs);
    }
    if (score.comboThresholds !== undefined) {
      assertArray(file, "waves.score.comboThresholds", score.comboThresholds);
    }
    if (score.comboMultipliers !== undefined) {
      assertArray(file, "waves.score.comboMultipliers", score.comboMultipliers);
    }
  }
}

function validateSynergies(data: unknown, file: string): void {
  const root = assertRecord(file, "synergies", data);
  const synergies = assertArray(file, "synergies.synergies", root.synergies);
  if (synergies.length === 0) invalid(file, "synergies.synergies", "is empty");
}

function validateCards(data: unknown, file: string): void {
  const root = assertRecord(file, "cards", data);
  const cards = assertArray(file, "cards.cards", root.cards);
  if (cards.length === 0) invalid(file, "cards.cards", "is empty");

  for (let i = 0; i < cards.length; i++) {
    const card = assertRecord(file, `cards.cards[${i}]`, cards[i]);
    assertString(file, `cards.cards[${i}].id`, card.id);
    const effect = assertRecord(file, `cards.cards[${i}].effect`, card.effect);
    assertString(file, `cards.cards[${i}].effect.type`, effect.type);
  }
}

// Mirrors validateCards: id + i18n keys + effect {type, value}; the effect
// type's semantics (and the value's exact-number contract, D-219) are owned
// by the MODULE_EFFECTS registry + the modules-schema test, not the loader.
function validateModules(data: unknown, file: string): void {
  const root = assertRecord(file, "modules", data);
  const modules = assertArray(file, "modules.modules", root.modules);
  if (modules.length === 0) invalid(file, "modules.modules", "is empty");

  for (let i = 0; i < modules.length; i++) {
    const module = assertRecord(file, `modules.modules[${i}]`, modules[i]);
    assertString(file, `modules.modules[${i}].id`, module.id);
    assertString(file, `modules.modules[${i}].name`, module.name);
    assertString(file, `modules.modules[${i}].description`, module.description);
    const effect = assertRecord(
      file,
      `modules.modules[${i}].effect`,
      module.effect
    );
    assertString(file, `modules.modules[${i}].effect.type`, effect.type);
    assertNumber(file, `modules.modules[${i}].effect.value`, effect.value);
    if (module.unlockCondition !== null) {
      assertString(
        file,
        `modules.modules[${i}].unlockCondition`,
        module.unlockCondition
      );
    }
  }
}

function validateFirewalls(data: unknown, file: string): void {
  const root = assertRecord(file, "firewalls", data);
  const defaults = assertRecord(file, "default", root.default);
  assertPositive(file, "default.cost", defaults.cost);
  assertPositive(file, "default.hp", defaults.hp);
  assertPositive(file, "default.maxHp", defaults.maxHp);
  if (root.maxFirewalls !== undefined) {
    assertPositive(file, "maxFirewalls", root.maxFirewalls);
  }
}
